import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AlertCircle, ArrowLeft } from 'lucide-react'
import { useCreator } from '@/hooks/use-creators'
import { useBreadcrumbLabel } from '@/hooks/use-breadcrumb-overrides'
import { CreatorHeader } from '@/components/creators/creator-header'
import { CreatorOverviewTab } from '@/components/creators/creator-overview-tab'
import { CreatorSalesTab } from '@/components/creators/creator-sales-tab'
import { SeedingTab } from '@/components/creators/seeding-tab'
import { PostsTab } from '@/components/creators/posts-tab'
import { CreatorSettingsTab } from '@/components/creators/creator-settings-tab'

const searchSchema = z.object({
  tab: z.enum(['overview', 'posts', 'sales', 'seedings', 'settings']).catch('overview'),
})

export const Route = createFileRoute('/_app/creators/$id')({
  component: CreatorDetailPage,
  validateSearch: searchSchema,
})

/**
 * Render the creator detail page, showing loading, error, or the creator's header with tabbed content.
 *
 * The component reads the route `id`, fetches the creator, and displays one of:
 * - skeleton placeholders while loading,
 * - an error view when fetching fails or the creator is not found,
 * - the creator header and a tabbed interface with `overview`, `sales`, `seedings`, `posts`, and `settings`.
 *
 * @returns A React element representing the creator detail page UI.
 */
function CreatorDetailPage() {
  const { id } = Route.useParams()
  const { tab } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { data: creator, isLoading, error } = useCreator(id)
  useBreadcrumbLabel(id, creator?.name)

  const setActiveTab = (value: string) => {
    navigate({ search: { tab: value as typeof tab }, replace: true })
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  if (error || !creator) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <AlertCircle className="h-10 w-10 text-destructive" />
        <h2 className="text-xl font-semibold">Creator não encontrado</h2>
        <p className="text-muted-foreground">O ID informado não existe.</p>
        <Button variant="outline" onClick={() => window.history.back()}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <CreatorHeader creator={creator} />

      <Tabs value={tab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4 w-full justify-start overflow-x-auto overflow-y-hidden">
          <TabsTrigger value="overview" className="shrink-0">
            Visão Geral
          </TabsTrigger>
          <TabsTrigger value="posts" className="shrink-0">
            Posts & Mídia
          </TabsTrigger>
          <TabsTrigger value="sales" className="shrink-0">
            Vendas
          </TabsTrigger>
          <TabsTrigger value="seedings" className="shrink-0">
            Seedings
          </TabsTrigger>
          <TabsTrigger value="settings" className="shrink-0">
            Configurações
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-0">
          <CreatorOverviewTab
            creator={creator}
            onNavigateToSettings={(scrollTo) => {
              setActiveTab('settings')
              if (scrollTo) {
                requestAnimationFrame(() => {
                  document
                    .getElementById(scrollTo)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                })
              }
            }}
          />
        </TabsContent>

        <TabsContent value="posts">
          <PostsTab
            creatorId={creator.id}
            authorUsername={creator.instagramUsername || creator.tiktokUsername || creator.name}
          />
        </TabsContent>

        <TabsContent value="sales">
          <CreatorSalesTab creatorId={creator.id} />
        </TabsContent>

        <TabsContent value="seedings">
          <SeedingTab
            creatorId={creator.id}
            creatorCpf={creator.cpf}
            creatorAddress={creator.address}
          />
        </TabsContent>

        <TabsContent value="settings">
          <CreatorSettingsTab creator={creator} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
